// ./src/components/layouts/DashboardLayout/SubscriptionFooter.tsx
import React, { useMemo } from "react";
import { Alert, Group, Text, Button } from "@mantine/core";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import { AppIcon } from "@/components/generic/AppIcon";
import { useBranding } from "@/contexts/BrandingContext";

import { FooterMessageType, FOOTER_ICON_MAP, FOOTER_COLOR_MAP } from "./constants";

// ============================================
// Types
// ============================================

interface SubscriptionFooterConfig {
  message: string;
  color: string;
  icon: string;
  buttonText: string;
}

interface SubscriptionFooterProps {
  subscriptionStatus: string | undefined;
  trialEndsAt: Date | null | undefined;
  footerMessage?: string;
  footerMessageType?: FooterMessageType;
  showFooterMessage?: boolean;
}

// ============================================
// Hook: useSubscriptionFooter
// ============================================

/**
 * Computes whether the footer should be shown and the subscription banner config.
 * Exported so the parent can use `hasFooter` for AppShell layout config.
 */
export const useSubscriptionFooter = ({
  subscriptionStatus,
  trialEndsAt,
  showFooterMessage = false,
}: Pick<SubscriptionFooterProps, "subscriptionStatus" | "trialEndsAt" | "showFooterMessage">) => {
  const location = useLocation();
  // A2e #101 — the brand from the white label, never a name written here.
  const brandName = useBranding().brand.name;

  const subscriptionConfig = useMemo((): SubscriptionFooterConfig | null => {
    if (location.pathname === "/money/subscription") return null;

    if (subscriptionStatus === "trial" && trialEndsAt) {
      const daysRemaining = dayjs(trialEndsAt).diff(dayjs(), "day");
      if (daysRemaining > 3) return null;

      return {
        message: `Your trial ends in ${daysRemaining} ${daysRemaining === 1 ? "day" : "days"}. Upgrade now to continue using ${brandName}.`,
        color: "warning",
        icon: "AlertTriangle",
        buttonText: "Upgrade Now",
      };
    }

    if (subscriptionStatus === "expired") {
      return {
        message: `Your trial has ended. Subscribe now to continue using ${brandName}.`,
        color: "error",
        icon: "AlertCircle",
        buttonText: "Subscribe Now",
      };
    }

    if (subscriptionStatus === "cancelled") {
      return {
        message: `Your subscription has been cancelled. Reactivate to continue using ${brandName}.`,
        color: "warning",
        icon: "AlertTriangle",
        buttonText: "Reactivate",
      };
    }

    return null;
  }, [location.pathname, subscriptionStatus, trialEndsAt, brandName]);

  const showSubscriptionBanner = Boolean(subscriptionConfig);
  const hasFooter = showFooterMessage || showSubscriptionBanner;

  return { subscriptionConfig, showSubscriptionBanner, hasFooter };
};

// ============================================
// Component
// ============================================

export const SubscriptionFooter: React.FC<SubscriptionFooterProps> = ({
  subscriptionStatus,
  trialEndsAt,
  footerMessage,
  footerMessageType = "info",
  showFooterMessage = false,
}) => {
  const navigate = useNavigate();
  const { subscriptionConfig, showSubscriptionBanner } = useSubscriptionFooter({
    subscriptionStatus,
    trialEndsAt,
    showFooterMessage,
  });

  // Subscription banner takes priority
  if (showSubscriptionBanner && subscriptionConfig) {
    return (
      <Alert
        icon={<AppIcon iconName={subscriptionConfig.icon} size={16} />}
        color={subscriptionConfig.color}
        variant="light"
        radius={0}
        h="100%"
        styles={{
          root: {
            border: "none",
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        <Group justify="space-between" w="100%" gap="sm" wrap="nowrap">
          <Text size="md" style={{ flex: 1 }}>
            {subscriptionConfig.message}
          </Text>
          <Button
            size="md"
            color={subscriptionConfig.color}
            onClick={() => navigate("/money/subscription")}
          >
            {subscriptionConfig.buttonText}
          </Button>
        </Group>
      </Alert>
    );
  }

  // Generic footer message fallback
  if (showFooterMessage && footerMessage) {
    return (
      <Alert
        icon={
          <AppIcon
            iconName={FOOTER_ICON_MAP[footerMessageType]}
            size={16}
          />
        }
        color={FOOTER_COLOR_MAP[footerMessageType]}
        variant="light"
        radius={0}
        h="100%"
        styles={{
          root: {
            border: "none",
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        {footerMessage}
      </Alert>
    );
  }

  return null;
};